import { useMemo, useState } from 'react';
import { useT } from '@hana/i18n';
import './i18n';
import { IconFileText, IconLock, IconMail, IconListDetails, IconSparkles, IconTrash, IconX, IconEye, IconDownload } from '@tabler/icons-react';
import { Button, StateView, type ScreenState } from '@hana/ui';
import { DOC_TYPES, DOMAINS, fileKind, filterDocs, fmtTs, liveDocs, trashDocs } from './logic';
import { KIND_CLS } from './kindCls';
import type { LibDoc } from './types';
import s from './files.module.css';
import l from './library.module.css';

// Mint-Files › tab=lib "Tài liệu công ty": cột trái = loại văn bản + nghiệp vụ, cột phải = danh sách tài liệu (thanh 56, đầu bảng 38).
// Chỉ đọc: nút ghi báo toast "Chưa mở ghi ở bản này".

const CM: Record<number, [string, string, string][]> = {
  1: [['NhiDTT', 'Áp cho C/O EUR.1 nộp từ 10/May/2026, bản cũ để tham chiếu lô trước.', '21/Oct 09:20']],
  2: [['MAITTN', 'Đã cập nhật v3 theo mẫu YML 10/2026.', '20/Oct 15:02'], ['PhuongHT', '@MAITTN bản này dùng cho ONE được không chị?', '21/Oct 09:10']],
  3: [['TranTPB', 'Đã gửi tóm tắt cho nhóm BKG và Sales.', '21/Oct 11:05']],
};
const docDate = (d: LibDoc, t: (k: string, v?: Record<string, string | number>) => string) => (d.deleted ? t('fl.lib.left', { n: d.left ?? 0 }) : d.exp ? t('fl.lib.until', { d: d.exp }) : d.eff || '—');

export function NoticeBanner({ n, onScan, onHide }: { n: number; onScan: () => void; onHide: () => void }) {
  const t = useT();
  return (
    <div role="status" className={l.notice}>
      <IconMail size={16} aria-hidden="true" />
      <div className={s.grow}><b>{t('fl.lib.notice', { n })}</b>{t('fl.lib.noticeSub')}</div>
      <Button variant="primary" size="sm" icon={<IconSparkles size={16} />} onClick={onScan}>{t('fl.lib.scan')}</Button>
      <Button variant="ghost" size="sm" aria-label={t('fl.lib.hide')} icon={<IconX size={16} />} onClick={onHide} />
    </div>
  );
}

export function LibraryCols({ docs, state, q, rowH, wide, banner, sel, setSel, noWrite, retry }: {
  docs: readonly LibDoc[]; banner: boolean; state: ScreenState; q: string; rowH: number; wide: boolean;
  sel: number | null; setSel: (id: number | null) => void; noWrite: () => void; retry: () => void;
}) {
  const t = useT();
  const [trash, setTrash] = useState(false);
  const [type, setType] = useState<string | null>(null);
  const [dom, setDom] = useState<string | null>(null);
  const [chk, setChk] = useState<Record<number, boolean>>({});
  const ok = state === 'ready' || state === 'empty';
  const live = liveDocs(docs);
  const rows = useMemo(() => (state === 'empty' ? [] : filterDocs(docs, { trash, type, dom, q })), [docs, state, trash, type, dom, q]);
  const picked = rows.filter((r) => chk[r.id]);
  const detail = sel === null ? null : docs.find((d) => d.id === sel) ?? null;
  const reset = () => { setSel(null); setChk({}); };
  const goAll = () => { setTrash(false); setType(null); setDom(null); reset(); };
  const nav = (key: string, label: string, Icon: typeof IconFileText, n: number | string, on: boolean, go: () => void) => (
    <button key={key} type="button" className={s.nav} aria-pressed={on} onClick={go}>
      <Icon size={16} aria-hidden="true" />
      <span className={s.navK}>{label}</span>
      {ok && n !== '' && <span className={s.navN}>{n}</span>}
    </button>
  );
  const chips = [type ? { k: t('fl.lib.fType', { v: type }), x: () => setType(null) } : null, dom ? { k: t('fl.lib.fDom', { v: dom }), x: () => setDom(null) } : null].filter((c): c is { k: string; x: () => void } => !!c);

  return (
    <div className={`${s.cols} ${banner ? l.afterBanner : ''}`}>
      <section className={s.c1} aria-label={t('fl.lib.typeDom')}>
        {nav('all', t('fl.lib.all'), IconFileText, live.length, !trash && !type && !dom, goAll)}
        {nav('trash', t('fl.lib.trash60'), IconTrash, trashDocs(docs).length, trash, () => { setTrash(true); setType(null); setDom(null); reset(); })}
        <div className={s.head}>{t('fl.lib.hType')}</div>
        {DOC_TYPES.map((k) => nav(`t:${k}`, k, IconFileText, live.filter((d) => d.type === k).length || '', type === k, () => { setTrash(false); setType(type === k ? null : k); setSel(null); }))}
        <div className={s.head}>{t('fl.lib.hDom')}</div>
        {DOMAINS.map((k) => nav(`d:${k}`, k, IconListDetails, live.filter((d) => d.dom === k).length || '', dom === k, () => { setTrash(false); setDom(dom === k ? null : k); setSel(null); }))}
      </section>

      <section className={s.c2} aria-label={t('fl.lib.docList')}>
        {picked.length === 0 ? (
          <div className={s.bar56}>
            <b className={l.title}>{trash ? t('fl.lib.trash') : t('fl.tabLib')}</b>
            <span className={l.sub}>{t('fl.lib.docsN', { n: rows.length })}{trash ? t('fl.lib.autoDel') : ''}</span>
            <span className={s.grow} />
            {chips.map((c) => <button key={c.k} type="button" className={l.chip} aria-label={t('fl.lib.unfilter', { v: c.k })} onClick={c.x}>{c.k} <IconX size={13} aria-hidden="true" /></button>)}
          </div>
        ) : (
          <div className={`${s.bar56} ${l.bulk}`} role="toolbar" aria-label={t('fl.lib.bulk')}>
            <b>{t('fl.chosen', { n: picked.length })}</b>
            {trash ? <Button variant="secondary" onClick={noWrite}>{t('fl.lib.restore')}</Button> : <Button variant="secondary" icon={<IconDownload size={16} />} onClick={noWrite}>{t('fl.lib.download')}</Button>}
            <Button variant="secondary" onClick={noWrite}>{trash ? t('fl.lib.delForever') : t('fl.lib.delTrash')}</Button>
            <span className={s.grow} />
            <Button variant="ghost" onClick={() => setChk({})}>{t('fl.lib.unselect')}</Button>
          </div>
        )}
        <div className={s.scroll}>
          <StateView
            state={state === 'ready' || state === 'noperm' ? 'ready' : state}
            title={state === 'error' ? t('fl.lib.errTitle') : trash ? t('fl.lib.trashEmpty') : t('fl.lib.none')}
            desc={state === 'error' ? t('fl.lib.errDesc') : trash ? t('fl.lib.trashDesc') : t('fl.lib.noneDesc')}
            action={state === 'error' ? <Button onClick={retry}>{t('fl.lib.retry')}</Button> : undefined}
          >
            {state === 'noperm' && <div className={l.ro}><b>{t('fl.lib.viewOnly')}</b></div>}
            <div className={s.th} role="row">
              <span className={s.w20} />
              <span className={s.grow}>{t('fl.lib.colDoc')}</span>
              <span className={l.w128}>{t('fl.lib.colType')}</span>
              <span className={l.w112}>{t('fl.lib.colDom')}</span>
              <span className={l.w104}>{trash ? t('fl.lib.colLeft') : t('fl.lib.colEff')}</span>
              <span className={`${l.w56} ${s.right}`}>{t('fl.lib.colVer')}</span>
            </div>
            {rows.map((d) => {
              const [k] = fileKind(d.n);
              const on = sel === d.id || !!chk[d.id];
              const snip = q && d.txt.includes(q.trim().toLowerCase()) ? t('fl.txtHit', { s: d.txt }) : d.deleted ? t('fl.lib.delBy', { n: d.deleted }) : d.sum;
              return (
                <div key={d.id} role="row" aria-selected={on} className={s.row} style={{ boxSizing: 'border-box', minHeight: rowH, height: 'auto', paddingBlock: 8 }}>
                  <span className={s.w20}><input type="checkbox" aria-label={t('fl.select', { n: d.title })} checked={!!chk[d.id]} onChange={() => setChk({ ...chk, [d.id]: !chk[d.id] })} /></span>
                  <button type="button" className={s.name} style={{ height: 'auto' }} onClick={() => setSel(d.id)}>
                    <span aria-hidden="true" className={`${s.kind} ${KIND_CLS[k]}`}>{k}</span>
                    <span className={s.nameTx}>
                      <span className={s.nameLine}><span className={s.nameN} style={{ fontWeight: 550 }}>{d.title}</span>{d.locked && <IconLock size={13} aria-label={t('fl.lib.locked')} />}{CM[d.id] && <span className={l.sub}>{t('fl.lib.comments', { n: CM[d.id]!.length })}</span>}</span>
                      <span className={s.snip}>{snip}</span>
                    </span>
                  </button>
                  <span className={`${l.w128} ${s.dim}`}>{d.type}</span>
                  <span className={l.w112}><span className={s.shc}>{d.dom}</span></span>
                  <span className={`${l.w104} ${l.wrap}`} style={d.deleted ? { color: 'var(--err)' } : undefined}>{docDate(d, t)}</span>
                  <span className={`${l.w56} ${s.right} ${s.dim}`}>v{d.ver}</span>
                </div>
              );
            })}
            {q && rows.length === 0 && <div className={s.none}>{t('fl.lib.noDocMatch', { q })}</div>}
          </StateView>
        </div>
        {wide && ok && <div className={s.foot}><span>{t('fl.lib.docsN', { n: rows.length })}</span></div>}
      </section>

      {!wide && detail && <aside className={l.aside} aria-label="Chi tiết"><DocPanel doc={detail} noWrite={noWrite} onClose={() => setSel(null)} /></aside>}
    </div>
  );
}

/** Chi tiết một tài liệu (cột phải 372 ở khổ rộng · thẻ 320 ở khổ hẹp). */
export function DocPanel({ doc, noWrite, onClose }: { doc: LibDoc; noWrite: () => void; onClose?: () => void }) {
  const t = useT();
  const del = !!doc.deleted;
  const info: [string, string][] = [[t('fl.lib.kEff'), doc.eff || '—'], [t('fl.lib.kExp'), doc.exp || t('fl.lib.noExp')], [t('fl.lib.kSrc'), doc.src], [t('fl.lib.kBy'), `${doc.by} · ${fmtTs(doc.ts)}`], [t('fl.lib.kViews'), String(doc.views)], [t('fl.lib.kVer'), `v${doc.ver}.0`]];
  return (
    <div className={l.panel}>
      <div className={l.pHead}>
        <div className={s.grow}><h2>{doc.title}</h2><div className={l.pFile}>{doc.n}</div></div>
        {onClose && <Button variant="ghost" aria-label={t('fl.lib.closePanel')} icon={<IconX size={16} />} onClick={onClose} />}
      </div>
      <div className={l.pChips}><span className={l.pType}>{doc.type}</span><span className={s.shc}>{doc.dom}</span>{doc.locked && <span className={s.shc}><IconLock size={12} aria-hidden="true" /> {t('fl.lib.lock')}</span>}</div>
      {del ? (
        <div className={l.pBtns2}><Button variant="primary" onClick={noWrite}>{t('fl.lib.restore')}</Button><Button variant="secondary" onClick={noWrite}>{t('fl.lib.delForever')}</Button></div>
      ) : (
        <div className={l.pBtns}><Button variant="primary" icon={<IconEye size={16} />} onClick={noWrite}>{t('fl.lib.view')}</Button><Button variant="secondary" icon={<IconDownload size={16} />} onClick={noWrite}>{t('fl.lib.download')}</Button><Button variant="secondary" onClick={noWrite}>{t('fl.lib.actions')}</Button></div>
      )}
      {del && <div className={l.pFile}>{t('fl.lib.delBy', { n: doc.deleted ?? '' })}</div>}
      <section className={l.ai}><div className={l.aiH}><IconSparkles size={14} aria-hidden="true" /> {t('fl.lib.aiSum')}</div><div>{doc.sum}</div></section>
      <dl className={l.dl}>{info.map(([k, v]) => <div key={k} className={l.dlRow}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
      <div className={l.tags}>{doc.tags.map((g) => <span key={g}>#{g}</span>)}</div>
      <section className={l.box}>
        <h3>{t('fl.lib.versions')}</h3>
        {Array.from({ length: Math.min(doc.ver, 3) }, (_, i) => (
          <div key={i} className={l.vRow}><b>v{doc.ver - i}.0</b>{i === 0 && <span className={l.actW}>{t('fl.lib.current')}</span>}<div className={l.actW}>{i === 0 ? `${fmtTs(doc.ts)} · ${doc.by}` : ['', '15/Sep/2026 10:00 · MAITTN', '02/Jun/2026 09:30 · MAITTN'][i]}</div></div>
        ))}
      </section>
      <section className={l.box}>
        <h3>{t('fl.lib.commentsN', { n: (CM[doc.id] ?? []).length })}</h3>
        {(CM[doc.id] ?? []).map(([who, txt, when]) => <div key={when} className={l.vRow}><div className={l.actW}><b>{who}</b> · {when}</div><div>{txt}</div></div>)}
      </section>
    </div>
  );
}
